import { useState, useMemo } from 'react';

import {
  HsFormFields,
  HubpotApiData,
  HubspotApiStatus,
  HubspotDataType,
  HubspotData,
} from '~app/pages/Pricing/components/HubSpotForm/types';

const EXCLUDE_COUNTRY_FIELD = 'contact_country__c';
const HUBSPOT_PROXY = '/cc/hs/';
const HUBSPOT_FORM_ID = '289edeb2-bb6b-45f7-bef3-7185f3c6e9f4';

const fetchHubSpotForm = async (): Promise<HubpotApiData> => {
  const formData = new URLSearchParams();
  formData.append('formId', HUBSPOT_FORM_ID);
  const fetchHSForms = await fetch(HUBSPOT_PROXY, {
    method: 'POST',
    body: formData,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
  return fetchHSForms.json();
};

function mapHiddenField({ name, hidden }: HubspotDataType): boolean {
  return name === EXCLUDE_COUNTRY_FIELD ? true : hidden;
}

function mapHubSpotFormFields<T extends HubspotDataType>(hsData: T): HubspotData {
  const { form } = hsData;
  const mappedData: HubspotData = {
    fields: [],
    redirect: form?.redirect,
    guid: form?.guid,
    portalId: form?.portalId,
  };

  const shallowCopiedFormFields: HubspotDataType[] = [...form?.formFieldGroups];

  const temp = shallowCopiedFormFields.map((field: HubspotDataType) => {
    return field?.fields.map((formFields: HubspotDataType): HsFormFields => {
      return {
        id: `HubSpotForm__${formFields.name}`,
        type: formFields.type,
        name: formFields.name,
        defaultValue: formFields.defaultValue,
        label: formFields.label,
        fieldType: formFields.fieldType,
        required: formFields.required,
        hidden: mapHiddenField(formFields),
        hideLabel: formFields.labelHidden,
        placeholder: formFields.placeholder,
      };
    });
  });
  return { ...mappedData, fields: temp.flat() };
}

export default function useHubSpotFormAPI<T extends Partial<T>>(): T | {} {
  const [data, setData] = useState<T | {}>({
    status: HubspotApiStatus.Loading,
    error: null,
    data: null,
  });
  useMemo(() => {
    fetchHubSpotForm()
      .then((formData: HubspotDataType) => {
        setData({
          data: mapHubSpotFormFields(formData),
          error: null,
          status: 'Success',
        });
      })
      .catch(err => {
        setData({
          data: null,
          status: HubspotApiStatus.Error,
          error: err,
        });
      });
  }, []);

  return data;
}
